import { firebaseApp } from "../firebaseConfig";
import { WEBOT_SERVER_ENDPOINT } from "../logic/Consts";
import type {
  AiChat as SharedAiChat,
  AiChatMessage as SharedAiChatMessage,
  AiChatRole,
  ChatMessage as SharedChatMessage,
  REST_schema,
} from "../@types/sharedSchema";

// Re-exports of canonical types from the shared schema (mirrored from the
// server repo). We keep the local names so existing call sites that import
// `AiChat` / `AiChatMessage` from this module compile unchanged. Date fields
// in the shared schema are typed as `Date` because that's the DB shape; the
// fetch helpers below adapt the ISO-string wire format to Date instances.
export type ChatRole = AiChatRole;
export type ChatMessage = SharedChatMessage;
export type AiChat = SharedAiChat;
export type AiChatMessage = SharedAiChatMessage;

export interface StreamEvent {
  type: string;
  data: any;
}

// --- Wire-format adapters ---
// Server serialises Firestore Timestamps as ISO8601 strings. The canonical
// types declare `Date`, so we hydrate the strings into Date instances at the
// network boundary — that way every consumer downstream sees the canonical
// shape, even though JSON only knows about strings.

const toDate = (v: unknown): Date | undefined => {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  const d = new Date(v as string);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

const parseAiChat = (wire: any): AiChat => ({
  ...wire,
  lastMessageAt: toDate(wire?.lastMessageAt) ?? new Date(0),
  createdTime: toDate(wire?.createdTime),
  updatedTime: toDate(wire?.updatedTime),
});

const parseAiChatMessage = (wire: any): AiChatMessage => ({
  ...wire,
  createdTime: toDate(wire?.createdTime),
  updatedTime: toDate(wire?.updatedTime),
});

const decoder = new TextDecoder();

const parseSseChunk = (
  raw: string
): { event: string; data: string } | null => {
  const lines = raw.split("\n");
  let event = "message";
  let data = "";
  for (const line of lines) {
    if (line.startsWith("event: ")) event = line.slice(7).trim();
    else if (line.startsWith("data: ")) data += line.slice(6);
  }
  if (!data && event === "message") return null;
  return { event, data };
};

async function authToken(): Promise<string> {
  const currentUser = firebaseApp.auth().currentUser;
  if (!currentUser) throw new Error("Not authenticated");
  return await currentUser.getIdToken();
}

// Surfaces as `TypeError: Failed to fetch` (Chrome), `NetworkError` (Firefox),
// `Load failed` (Safari). We retry these because they mean the request never
// produced a response — the backend persists the user message only after the
// request body lands, so a network-aborted POST didn't write anything to
// Firestore. Once we have a `Response` object, retrying is unsafe (server may
// already be mid-write).
const isTransientNetworkError = (err: unknown): boolean => {
  if (!(err instanceof TypeError)) return false;
  const msg = err.message || "";
  return /failed to fetch|network|load failed/i.test(msg);
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function* streamChat(
  messages: ChatMessage[],
  options?: { chatId?: string; signal?: AbortSignal }
): AsyncGenerator<StreamEvent> {
  const token = await authToken();

  const body: REST_schema.ChatStreamRequest = { messages, chatId: options?.chatId };

  // Retry the initial POST 2 extra times (total 3 attempts) on transient
  // network errors — flaky mobile network handoffs and Cloud Run cold-starts
  // both surface as `Failed to fetch`. Customer-reported bug (chat 64227fb0)
  // was a single failed POST silently losing the user's follow-up question.
  let response: Response | undefined;
  let lastErr: unknown;
  const MAX_ATTEMPTS = 3;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      response = await fetch(`${WEBOT_SERVER_ENDPOINT}/api/v1/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(body),
        signal: options?.signal,
      });
      break;
    } catch (err) {
      lastErr = err;
      if (options?.signal?.aborted) throw err; // user hit Stop — don't retry
      if (!isTransientNetworkError(err) || attempt === MAX_ATTEMPTS) throw err;
      // Short backoff: 400ms, 1200ms. Total worst-case extra wait ~1.6s.
      await sleep(attempt === 1 ? 400 : 1200);
    }
  }
  if (!response) throw (lastErr instanceof Error ? lastErr : new Error("Chat request failed"));

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Chat request failed (${response.status}): ${text}`);
  }
  if (!response.body) throw new Error("No response body");

  const reader = response.body.getReader();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const chunks = buffer.split("\n\n");
    buffer = chunks.pop() ?? "";

    for (const chunk of chunks) {
      const parsed = parseSseChunk(chunk);
      if (!parsed) continue;
      if (parsed.event === "done") return;
      if (parsed.event === "error") {
        const err = JSON.parse(parsed.data || "{}");
        throw new Error(err.message || "Chat stream error");
      }
      // Named `chat` event carries the resolved chatId. We surface it as a
      // synthetic type so the panel can update routing/state without having
      // to special-case named-vs-default events upstream.
      if (parsed.event === "chat") {
        try {
          const data = JSON.parse(parsed.data);
          yield { type: "chat_meta", data };
        } catch {
          // ignore malformed
        }
        continue;
      }
      try {
        yield JSON.parse(parsed.data) as StreamEvent;
      } catch {
        // Ignore malformed lines
      }
    }
  }
}

export interface TranscribeAudioParams {
  audioBase64: string;
  mimeType: string;
  /** Existing input text — passed as a terminology hint to Gemini. */
  contextHint?: string;
  signal?: AbortSignal;
}

export async function transcribeAudio({
  audioBase64,
  mimeType,
  contextHint,
  signal,
}: TranscribeAudioParams): Promise<string> {
  const currentUser = firebaseApp.auth().currentUser;
  if (!currentUser) throw new Error("Not authenticated");
  const token = await currentUser.getIdToken();

  const body: REST_schema.TranscribeVoiceRequest = {
    audio: audioBase64,
    mimeType,
    contextHint,
  };
  const response = await fetch(
    `${WEBOT_SERVER_ENDPOINT}/api/v1/chat/transcribe`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify(body),
      signal,
    }
  );

  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(`Transcription failed (${response.status}): ${errText}`);
  }
  const json = (await response.json()) as REST_schema.TranscribeVoiceResponse;
  return json.text ?? "";
}

// --- Chat persistence client ---

export async function listChats(limit = 50): Promise<AiChat[]> {
  const token = await authToken();
  const res = await fetch(
    `${WEBOT_SERVER_ENDPOINT}/api/v1/chat?limit=${limit}`,
    { headers: { Authorization: token } }
  );
  if (!res.ok) throw new Error(`listChats failed (${res.status})`);
  const body = await res.json();
  return ((body.chats ?? []) as any[]).map(parseAiChat);
}

export async function getChat(
  chatId: string
): Promise<{ chat: AiChat; messages: AiChatMessage[] }> {
  const token = await authToken();
  const res = await fetch(
    `${WEBOT_SERVER_ENDPOINT}/api/v1/chat/${encodeURIComponent(chatId)}`,
    { headers: { Authorization: token } }
  );
  if (!res.ok) throw new Error(`getChat failed (${res.status})`);
  const body = await res.json();
  return {
    chat: parseAiChat(body.chat),
    messages: ((body.messages ?? []) as any[]).map(parseAiChatMessage),
  };
}

export async function deleteChat(chatId: string): Promise<void> {
  const token = await authToken();
  const res = await fetch(
    `${WEBOT_SERVER_ENDPOINT}/api/v1/chat/${encodeURIComponent(chatId)}`,
    { method: "DELETE", headers: { Authorization: token } }
  );
  if (!res.ok) throw new Error(`deleteChat failed (${res.status})`);
}

// --- Admin AI-review (gated server-side by validateWebotAdmin) ---

// Wire shape sourced from the shared REST schema. Aliased so existing call
// sites (`AdminAiUser`) keep working without touching the import.
export type AdminAiUser = REST_schema.AdminAiUserActivity;

export async function listAdminAiUsers(): Promise<AdminAiUser[]> {
  const token = await authToken();
  const res = await fetch(
    `${WEBOT_SERVER_ENDPOINT}/api/v1/chat/admin/users`,
    { headers: { Authorization: token } }
  );
  if (!res.ok) throw new Error(`listAdminAiUsers failed (${res.status})`);
  const body = await res.json();
  return body.users ?? [];
}

export async function listAdminChatsForUser(userId: string): Promise<AiChat[]> {
  const token = await authToken();
  const res = await fetch(
    `${WEBOT_SERVER_ENDPOINT}/api/v1/chat/admin/users/${encodeURIComponent(userId)}/chats`,
    { headers: { Authorization: token } }
  );
  if (!res.ok) throw new Error(`listAdminChatsForUser failed (${res.status})`);
  const body = await res.json();
  return ((body.chats ?? []) as any[]).map(parseAiChat);
}

export async function getAdminChat(
  chatId: string
): Promise<{ chat: AiChat; messages: AiChatMessage[] }> {
  const token = await authToken();
  const res = await fetch(
    `${WEBOT_SERVER_ENDPOINT}/api/v1/chat/admin/chats/${encodeURIComponent(chatId)}`,
    { headers: { Authorization: token } }
  );
  if (!res.ok) throw new Error(`getAdminChat failed (${res.status})`);
  const body = await res.json();
  return {
    chat: parseAiChat(body.chat),
    messages: ((body.messages ?? []) as any[]).map(parseAiChatMessage),
  };
}

// --- Chat preferences ---

export type ChatActionPolicy = "confirm" | "auto" | "off";

export interface ChatPreferences {
  /** Whether the AI emits rich inline components (cards, action buttons). */
  richComponentsEnabled: boolean;
  /** How to handle data-mutating actions. See backend for full semantics. */
  actionPolicy: ChatActionPolicy;
}

export const CHAT_PREFERENCES_DEFAULTS: ChatPreferences = {
  richComponentsEnabled: true,
  actionPolicy: "confirm",
};

export async function getChatPreferences(): Promise<ChatPreferences> {
  const token = await authToken();
  const res = await fetch(
    `${WEBOT_SERVER_ENDPOINT}/api/v1/chat/preferences`,
    { headers: { Authorization: token } }
  );
  if (!res.ok) throw new Error(`getChatPreferences failed (${res.status})`);
  const body = (await res.json()) as { preferences: ChatPreferences };
  return body.preferences;
}

export async function updateChatPreferences(
  patch: Partial<ChatPreferences>
): Promise<ChatPreferences> {
  const token = await authToken();
  const reqBody: REST_schema.UpdateChatPreferencesRequest = patch;
  const res = await fetch(
    `${WEBOT_SERVER_ENDPOINT}/api/v1/chat/preferences`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify(reqBody),
    }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`updateChatPreferences failed (${res.status}): ${text}`);
  }
  const body = (await res.json()) as REST_schema.UpdateChatPreferencesResponse;
  return body.preferences;
}

// --- Chat action dispatcher ---

export interface ExecuteChatActionResult {
  ok: boolean;
  type: string;
  result?: unknown;
  error?: string;
}

/**
 * Executes a confirmed action button (<webot-action />). The backend dispatches
 * by `type` to the underlying service. Errors come back with ok=false and an
 * error message — we don't throw on action-level failures so the button can
 * render an inline error pill instead of bubbling up.
 */
export async function executeChatAction(
  type: string,
  params: Record<string, unknown>
): Promise<ExecuteChatActionResult> {
  const token = await authToken();
  const reqBody: REST_schema.ExecuteChatActionRequest = { type, params };
  const res = await fetch(`${WEBOT_SERVER_ENDPOINT}/api/v1/chat/actions`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token },
    body: JSON.stringify(reqBody),
  });
  // 4xx with a JSON body still parses — the backend uses 400 for action
  // failures. Network/auth errors throw normally below.
  let body: any = {};
  try {
    body = await res.json();
  } catch {
    // ignore
  }
  if (!res.ok && !body?.error) {
    throw new Error(`Action failed (${res.status})`);
  }
  return body as ExecuteChatActionResult;
}
